import { useGateValue } from "@statsig/react-bindings";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useAtom, useSetAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { ChevronsUpDown, Copy, Download, Eye, EyeOff, Link } from "lucide-react";
import { useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSearchParams } from "wouter";
import { useShellUrlWriter } from "@/components/navigation/shellUrl.ts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import type { ThreadDebugData } from "@/types/thread.ts";

/** Generates a timestamped filename for debug JSON downloads */
export const generateDebugFilename = (threadId: number): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `thread-${threadId}-debug-${timestamp}.json`;
};

/** Triggers a file download in the browser */
export const downloadJson = (data: unknown, filename: string): void => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const useCreateDebugLink = (threadId: number) => {
  const createDebugLinkMutation = useMutation({
    mutationFn: async ({ threadId }: { threadId: number }) => {
      const response = await api.post<{ id: string; url: string }>(
        `/api/a/threads/${threadId}/messages/debug/list`,
      );
      return response.data;
    },
  });

  const handleCreateDebugLink = useCallback(async () => {
    try {
      // Note: we must nest the promise inside ClipboardItem, not await it before,
      // otherwise Safari will reject the clipboard write due to expired user activation.
      // See: https://bugs.webkit.org/show_bug.cgi?id=222262
      const clipboardPromise = createDebugLinkMutation
        .mutateAsync({ threadId })
        .then((data) => new Blob([data.url], { type: "text/plain" }));

      await navigator.clipboard.write([new ClipboardItem({ "text/plain": clipboardPromise })]);
      confirmationToast("Link copied to clipboard");
    } catch (error) {
      console.error("Failed to generate debug link:", error);
      errorToast("Failed to generate debug link");
    }
  }, [threadId, createDebugLinkMutation]);

  return {
    handleCreateDebugLink,
    isPending: createDebugLinkMutation.isPending,
  };
};

export const useFetchDebugData = (threadId: number) => {
  const fetchDebugDataMutation = useMutation({
    mutationFn: async ({ threadId }: { threadId: number }) => {
      const response = await api.get<ThreadDebugData>(
        `/api/a/threads/${threadId}/messages/debug/data`,
      );
      return response.data;
    },
  });

  const handleCopyDebugJson = useCallback(async () => {
    try {
      // Use ClipboardItem with Promise for Safari compatibility
      const clipboardPromise = fetchDebugDataMutation
        .mutateAsync({ threadId })
        .then((data) => new Blob([JSON.stringify(data, null, 2)], { type: "text/plain" }));

      await navigator.clipboard.write([new ClipboardItem({ "text/plain": clipboardPromise })]);
      confirmationToast("Debug JSON copied to clipboard");
    } catch (error) {
      console.error("Failed to copy debug JSON:", error);
      errorToast("Failed to copy debug JSON");
    }
  }, [threadId, fetchDebugDataMutation]);

  const handleDownloadDebugJson = useCallback(async () => {
    try {
      const filename = generateDebugFilename(threadId);

      // Create clipboard promise that resolves immediately with filename.
      // This must be created synchronously in the user gesture for Safari compatibility.
      const clipboardPromise = Promise.resolve(new Blob([filename], { type: "text/plain" }));

      // Start clipboard write and fetch in parallel - clipboard write must be initiated
      // in the user gesture context for Safari
      const [, data] = await Promise.all([
        navigator.clipboard.write([new ClipboardItem({ "text/plain": clipboardPromise })]),
        fetchDebugDataMutation.mutateAsync({ threadId }),
      ]);

      downloadJson(data, filename);
      confirmationToast(`Downloaded ${filename} (filename copied)`);
    } catch (error) {
      console.error("Failed to download debug JSON:", error);
      errorToast("Failed to download debug JSON");
    }
  }, [threadId, fetchDebugDataMutation]);

  return {
    handleCopyDebugJson,
    handleDownloadDebugJson,
    isPending: fetchDebugDataMutation.isPending,
  };
};

const EVENT_VIEW_STORAGE_KEY = "preferred-thread-view";

/**
 * Event view toggle, gated by the `debug_link_menu` Statsig flag. URL (?view=)
 * takes precedence, then localStorage preference, then defaults to messages view.
 */
export const useEventView = () => {
  const debugMenuEnabled = useGateValue("debug_link_menu");

  const [searchParams] = useSearchParams();
  const writeShellUrl = useShellUrlWriter();
  const viewParam = searchParams.get("view");

  const defaultView = localStorage.getItem(EVENT_VIEW_STORAGE_KEY) ?? "messages";
  const isEventView =
    debugMenuEnabled && (viewParam !== null ? viewParam === "events" : defaultView === "events");

  const toggleEventView = useCallback(() => {
    if (!debugMenuEnabled) return;
    const newView = isEventView ? "messages" : "events";
    localStorage.setItem(EVENT_VIEW_STORAGE_KEY, newView);
    writeShellUrl({ params: { view: newView } }, { replace: true });
  }, [debugMenuEnabled, isEventView, writeShellUrl]);

  return { isEventView, toggleEventView };
};

/**
 * How aggressively `ChildClusterCollapsible` auto-expands in the event view.
 * Cycling re-evaluates every mounted cluster against the new mode (manual clicks
 * stick within a mode, get reset on the next cycle). See `EventItems.tsx`.
 */
export type AutoExpandMode = "minimal" | "expanded" | "all";

const AUTO_EXPAND_STORAGE_KEY = "event-auto-expand";
export const AUTO_EXPAND_ORDER: AutoExpandMode[] = ["minimal", "expanded", "all"];

export const autoExpandAtom = atomWithStorage<AutoExpandMode>(AUTO_EXPAND_STORAGE_KEY, "minimal");

/** Returns a stable callback that advances `autoExpandAtom` to the next mode. */
export const useCycleAutoExpand = () => {
  const setAutoExpandMode = useSetAtom(autoExpandAtom);
  return useCallback(() => {
    setAutoExpandMode(
      (cur) => AUTO_EXPAND_ORDER[(AUTO_EXPAND_ORDER.indexOf(cur) + 1) % AUTO_EXPAND_ORDER.length],
    );
  }, [setAutoExpandMode]);
};

/**
 * Registers the two event-view keyboard shortcuts:
 * - ⌘⌥V toggles between messages and events view
 * - ⌘⌥E cycles the auto-expand mode (only active in events view)
 *
 * Call from any page that hosts a `MessageList` (Thread, ThreadDebug, …) so
 * admins get a consistent set of shortcuts.
 */
export const useEventViewHotkeys = () => {
  const { isEventView, toggleEventView } = useEventView();
  const cycleAutoExpand = useCycleAutoExpand();

  useHotkeys("mod+alt+v", toggleEventView, {
    enableOnFormTags: ["TEXTAREA"],
    preventDefault: true,
  });
  useHotkeys("mod+alt+e", cycleAutoExpand, {
    enabled: isEventView,
    enableOnFormTags: ["TEXTAREA"],
    preventDefault: true,
  });
};

interface DebugLinkMenuProps {
  threadId: number;
  children: React.ReactNode;
}

export const DebugLinkMenu: React.FC<DebugLinkMenuProps> = ({ threadId, children }) => {
  const { isEventView, toggleEventView } = useEventView();
  const debugMenuEnabled = useGateValue("debug_link_menu");
  const [autoExpand] = useAtom(autoExpandAtom);
  const cycleAutoExpand = useCycleAutoExpand();
  const { handleCreateDebugLink, isPending: isCreatingLink } = useCreateDebugLink(threadId);
  const {
    handleCopyDebugJson,
    handleDownloadDebugJson,
    isPending: isFetchingData,
  } = useFetchDebugData(threadId);

  if (!debugMenuEnabled) {
    return null;
  }

  const isPending = isCreatingLink || isFetchingData;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={toggleEventView}>
          {isEventView ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          {isEventView ? "Show only messages" : "Show all events"}
          <DropdownMenuShortcut>⌘⌥V</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={cycleAutoExpand}
          disabled={!isEventView}
          title={isEventView ? undefined : "Requires event view"}
        >
          <ChevronsUpDown className="size-4" />
          Auto-expand: {autoExpand}
          <DropdownMenuShortcut>⌘⌥E</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleCreateDebugLink} disabled={isPending}>
          <Link className="size-4" />
          Create debug link
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopyDebugJson} disabled={isPending}>
          <Copy className="size-4" />
          Copy debug JSON
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDownloadDebugJson} disabled={isPending}>
          <Download className="size-4" />
          Download debug JSON
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
